import {
  CalendarOutlined,
  ClockCircleOutlined,
  DashOutlined,
  FilterFilled,
  HourglassOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { TableProps } from "antd";
import {
  Card,
  Checkbox,
  DatePicker,
  Flex,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React from "react";
import { AttendanceStatus } from "../../enum/common";
import { IAttendanceQuery, IAttendanceRecord } from "../../models/Hrm";
import { IPaginatedResponse } from "../../models/Pagination";
import { getAttendanceStatus } from "../filter/FilterEnum";
import { getTimeShift } from "../helper/attendance.helper";

const { Text } = Typography;

interface GenericLeaveTableProps {
  data?: IPaginatedResponse<IAttendanceRecord>;
  isLoading?: boolean;
  filter?: IAttendanceQuery;
  setFilters?: React.Dispatch<React.SetStateAction<IAttendanceQuery>>;
}

const HrmAttendanceTable: React.FC<GenericLeaveTableProps> = ({
  data,
  filter,
  isLoading,
  setFilters,
}) => {
  const dataSource = React.useMemo(() => {
    if (!data?.data?.length) return [];

    const getMonday = (date: dayjs.Dayjs) => {
      const d = date.day();
      if (d === 0) return date.subtract(6, "day").startOf("day");
      return date.subtract(d - 1, "day").startOf("day");
    };

    if (filter?.workDate) {
      const weekStart = getMonday(dayjs(filter.workDate));
      const result = [];

      for (let i = 0; i < 7; i++) {
        const currentDate = weekStart.add(i, "day");
        const attendance = data?.data?.find((item) =>
          dayjs(item.checkIn).isSame(currentDate, "day"),
        );

        result.unshift(
          attendance || {
            id: `empty-${currentDate.format("YYYY-MM-DD")}`,
            checkIn: currentDate.toISOString(),
            checkOut: null,
            isEmpty: true,
            status: [],
            lateMinutes: 0,
            earlyLeaveMinutes: 0,
            overtime: { totalHours: 0 },
          },
        );
      }
      return result as IAttendanceRecord[];
    }

    const result: IAttendanceRecord[] = [];

    const sortedData = [...data.data].sort(
      (a, b) => dayjs(b.checkIn).valueOf() - dayjs(a.checkIn).valueOf(),
    );

    const maxDate = dayjs(sortedData[0].checkIn).startOf("day");
    const minDate = dayjs(sortedData[sortedData.length - 1].checkIn).startOf(
      "day",
    );

    let currentDate = maxDate;

    while (currentDate.valueOf() >= minDate.valueOf()) {
      const dateToCompare = currentDate.clone();
      const recordsForDay = sortedData.filter((item) =>
        dayjs(item.checkIn).isSame(dateToCompare, "day"),
      );

      if (recordsForDay.length > 0) {
        recordsForDay.sort(
          (a, b) => dayjs(b.checkIn).valueOf() - dayjs(a.checkIn).valueOf(),
        );
        result.push(...recordsForDay);
      } else {
        result.push({
          id: `empty-${currentDate.format("YYYY-MM-DD")}`,
          checkIn: currentDate.toISOString(),
          checkOut: null,
          isEmpty: true,
          status: [],
          lateMinutes: 0,
          earlyLeaveMinutes: 0,
          overtime: { totalHours: 0 },
        } as unknown as IAttendanceRecord);
      }

      currentDate = currentDate.subtract(1, "day");
    }

    return result;
  }, [data?.data, filter?.workDate]);
  const debouncedSearch = React.useMemo(
    () =>
      debounce(
        <K extends keyof IAttendanceQuery>(
          field: K,
          value: IAttendanceQuery[K],
        ) => {
          setFilters?.((prev) => ({
            ...prev,
            [field]: value ?? undefined,
            page: 1,
          }));
        },
        300,
      ),
    [setFilters],
  );
  const handleSearch =
    <K extends keyof IAttendanceQuery>(field: K) =>
    (value: IAttendanceQuery[K]) => {
      debouncedSearch(field, value);
    };
  const columns: TableProps<IAttendanceRecord>["columns"] = [
    {
      title: filter?.workDate
        ? (() => {
            const d = dayjs(filter.workDate).day();
            const weekStart =
              d === 0
                ? dayjs(filter.workDate).subtract(6, "day")
                : dayjs(filter.workDate).subtract(d - 1, "day");
            const weekEnd = weekStart.add(6, "day");
            return `Tuần (${weekStart.format("DD/MM")} - ${weekEnd.format("DD/MM/YYYY")})`;
          })()
        : "Ngày trong tuần",
      key: "date",
      filteredValue: filter?.workDate ? [filter.workDate] : null,
      width: 200,
      render: (_, record: any) => {
        const days = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
        const dateObj = dayjs(record.checkIn);
        const dayOfWeek = dateObj.day();

        let textColor = "inherit";
        if (dayOfWeek === 0) textColor = "#cf1322";
        else if (dayOfWeek === 6) textColor = "#fa8c16";

        return (
          <Space className="font-medium">
            <CalendarOutlined
              style={{ color: textColor !== "inherit" ? textColor : undefined }}
            />
            <Text
              style={{ color: textColor !== "inherit" ? textColor : undefined }}
            >
              {`${days[dayOfWeek]}, ${dateObj.format("DD/MM/YYYY")}`}
            </Text>
          </Space>
        );
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <DatePicker
          picker="week"
          placeholder="Chọn tuần..."
          value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
          onChange={(date) => {
            const dateStr = date ? dayjs(date).format("YYYY-MM-DD") : "";
            setSelectedKeys(dateStr ? [dateStr] : []);
            handleSearch("workDate")(dateStr);
            confirm();
          }}
          allowClear
        />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    },
    {
      title: "Vào ca",
      dataIndex: "checkIn",
      key: "checkIn",
      render: (checkIn, record: any) => {
        if (record.isEmpty) return <DashOutlined />;
        return (
          <Tag icon={<ClockCircleOutlined />} color="success">
            {dayjs(checkIn).format("HH:mm")}
          </Tag>
        );
      },
    },
    {
      title: "Ra ca",
      dataIndex: "checkOut",
      key: "checkOut",
      render: (checkOut, record: any) => {
        if (record.isEmpty || !checkOut) return <DashOutlined />;
        return (
          <Tag icon={<ClockCircleOutlined />} color="warning">
            {dayjs(checkOut).format("HH:mm")}
          </Tag>
        );
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Card size="small" className="w-72 rounded-2xl shadow-lg border-0">
          <Flex vertical gap={16}>
            <Flex align="center" justify="space-between">
              <Text strong className="text-sm">
                Trạng thái chấm công
              </Text>
              <Tag color="blue">{selectedKeys?.length || 0} chọn</Tag>
            </Flex>
            <Checkbox.Group
              value={selectedKeys}
              className="w-full"
              onChange={(checkedValues) => {
                const values = checkedValues as AttendanceStatus[];
                setSelectedKeys(values);
                setFilters?.((prev) => ({
                  ...prev,
                  status: values.length > 0 ? values : undefined,
                  page: 1,
                }));
              }}
            >
              <Flex vertical gap={10}>
                {Object.values(AttendanceStatus).map((status) => {
                  const item = getAttendanceStatus(status);
                  return (
                    <Checkbox value={status}>
                      <Flex align="center" gap={10}>
                        <Tag color={item.color} className="m-0">
                          {item.label}
                        </Tag>
                      </Flex>
                    </Checkbox>
                  );
                })}
              </Flex>
            </Checkbox.Group>
          </Flex>
        </Card>
      ),
      render: (status: AttendanceStatus[], record: any) => {
        if (record.isEmpty) return <DashOutlined />;
        const validStatus = status.filter((item: AttendanceStatus) =>
          Object.values(AttendanceStatus).includes(item),
        );

        return (
          <>
            {validStatus.map((item: AttendanceStatus, idx: number) => (
              <>
                <Tag
                  key={idx}
                  style={{ marginRight: 8 }}
                  color={getAttendanceStatus(item).color}
                >
                  {getAttendanceStatus(item).label}
                </Tag>
              </>
            ))}
          </>
        );
      },
    },

    {
      title: "Tổng giờ",
      key: "totalTime",
      width: 200,
      render: (_, record: any) => {
        if (record.isEmpty || !record.checkOut) return <DashOutlined />;
        const checkInDate = new Date(record.checkIn);
        const shift = getTimeShift(checkInDate);
        checkInDate.setHours(shift.stdIn.getHours());
        checkInDate.setMinutes(shift.stdIn.getMinutes());

        const diffInMinutes = Math.round(
          (new Date(record.checkOut).getTime() - checkInDate.getTime()) /
            (1000 * 60),
        );

        const hours = Math.max(Math.floor(diffInMinutes / 60), 0);
        const minutes = Math.max(diffInMinutes % 60, 0);
        return (
          <Space>
            <HourglassOutlined />
            <Text strong>
              {hours}h {minutes}m
            </Text>
          </Space>
        );
      },

      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    },
    {
      title: "Tăng ca",
      dataIndex: ["overtime", "totalHours"],
      key: "overtime",
      width: 200,
      filteredValue:
        filter?.isOvertime !== undefined ? [filter.isOvertime] : null,
      render: (_, record: any) => {
        if (record.isEmpty || record?.overtime?.totalHours <= 0)
          return <DashOutlined />;
        return <Tag color="processing">{record?.overtime?.totalHours} h</Tag>;
      },

      filterIcon: (filtered: boolean) => (
        <FilterFilled style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    },
    {
      title:
        filter?.isLate !== undefined
          ? `Đi muộn (${filter.isLate ? "Có" : "Không"})`
          : "Đi muộn",
      dataIndex: "lateMinutes",
      key: "lateMinutes",
      width: 200,
      filteredValue: filter?.isLate !== undefined ? [filter.isLate] : null,
      render: (lateMinutes, record: any) => {
        if (record.isEmpty || lateMinutes <= 0) return <DashOutlined />;
        return <Tag color="error">Muộn {lateMinutes}p</Tag>;
      },
      filterDropdown: () => (
        <>
          <Select
            allowClear
            style={{ width: 120 }}
            placeholder="Chọn..."
            value={filter?.isLate}
            options={[
              { label: "Không", value: 0 },
              { label: "Có", value: 1 },
            ]}
            onChange={(e) => {
              handleSearch("isLate")(e);
            }}
          />
        </>
      ),
      filterIcon: (filtered: boolean) => (
        <FilterFilled style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    },
    {
      title:
        filter?.isEarly !== undefined
          ? `Về sớm (${filter.isEarly ? "Có" : "Không"})`
          : "Về sớm",
      key: "earlyLeaveMinutes",
      filteredValue: filter?.isEarly !== undefined ? [filter.isEarly] : null,
      width: 200,
      render: (_, record: any) => {
        if (record.isEmpty || record.earlyLeaveMinutes <= 0)
          return <DashOutlined />;
        return <Tag color="error">Sớm {record.earlyLeaveMinutes}p</Tag>;
      },

      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <>
          <Select
            allowClear
            style={{ width: 120 }}
            placeholder="Chọn..."
            value={filter?.isEarly}
            options={[
              { label: "Không", value: 0 },
              { label: "Có", value: 1 },
            ]}
            onChange={(e) => {
              handleSearch("isEarly")(e);
            }}
          />
        </>
      ),
      filterIcon: (filtered: boolean) => (
        <FilterFilled style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    },
  ];

  return (
    <div className="flex-1  p-4 overflow-auto h-full">
      <Flex vertical gap="large">
        <Table
          loading={isLoading}
          scroll={{ x: 1000 }}
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          pagination={false}
          className="shadow-sm border border-gray-100 overflow-hidden"
          rowClassName={(record: any) => {
            const dayOfWeek = dayjs(record.checkIn).day();
            if (dayOfWeek === 0) return "!bg-[#fff1f0]";
            if (dayOfWeek === 6) return "!bg-[#fff7e6]";
            return "";
          }}
        />
        {data?.pagination && (
          <Flex justify="end">
            <Pagination
              current={data.pagination.page}
              pageSize={data.pagination.limit}
              total={data.pagination.total}
              size="default"
              showSizeChanger
              onChange={(page, pageSize) => {
                setFilters?.({
                  ...filter,
                  page: page,
                  limit: pageSize,
                });
              }}
            />
          </Flex>
        )}
      </Flex>
    </div>
  );
};

export default HrmAttendanceTable;
